import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid, NumberedFlow } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Transactions & Gas',
    tagline: 'How a signed message becomes a fee-metered state change.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what fields a transaction actually commits to via the signature
//   Q1 - what happens to gas already spent when execution reverts
//   Q2 - the roles of base fee vs. priority fee under EIP-1559
export const blocks: StageBlock[] = [
    content('anatomy', () => (
        <StageSection gradientBorder eyebrow="Stage 3 of 8" title="What's actually inside a transaction">
            <Prose>
                A transaction is a small, signed data structure, not a network request in the HTTP sense. The
                sender's wallet builds it locally, signs it with the private key, and only then broadcasts the
                already-final bytes to the network.
            </Prose>
            <FactGrid items={[
                ['to', 'The recipient address: an EOA for a plain transfer, or a contract for a call. Empty for a contract deployment.'],
                ['value', 'How much ETH (in wei) to send along with the call.'],
                ['data', 'Arbitrary bytes. For a contract call, this encodes which function to run and with what arguments.'],
                ['nonce, gasLimit, gasPrice/fees', 'Ordering and fee-market fields covered below.'],
            ]} />
        </StageSection>
    )),

    content('signature', () => (
        <StageSection title="The signature commits to everything">
            <Prose>
                The sender signs a hash of the whole transaction: recipient, value, data, nonce, gas fields, and
                the target chain ID. Change a single byte of any of those fields and the signature no longer
                verifies. That's what makes a transaction tamper-evident in transit: a node can't quietly redirect
                your ETH to a different address, because doing so would invalidate your signature.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('gas', () => (
        <StageSection title="Gas: metering computation, not just bandwidth">
            <Prose>
                Every EVM operation (storage writes, arithmetic, hashing, external calls) has a fixed gas cost.
                Before execution, the sender sets a <strong>gas limit</strong> (the most gas they're willing to let
                the transaction consume). If execution runs out of gas partway through, the EVM reverts every state
                change the transaction made, but the gas already consumed up to that point is still paid to the
                network. Gas isn't refundable just because the transaction failed.
            </Prose>
            <KeyPoints items={[
                <>Gas exists to stop infinite loops and denial-of-service spam: unbounded computation always costs the attacker money.</>,
                <>A gas limit set too low causes an "out of gas" revert; a limit that's high enough just leaves unused gas refunded.</>,
                <>A revert undoes state changes, but it does not undo the gas fee already spent. This is a common source of confusion when debugging failed transactions.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('fee-market', () => (
        <StageSection title="Paying for gas: the EIP-1559 fee market">
            <Prose>
                Since the EIP-1559 upgrade, every block has a <strong>base fee</strong> per unit of gas, calculated
                automatically from how full the previous block was, and it is burned rather than paid to anyone. On
                top of that, senders add a small <strong>priority fee</strong> (a tip) that goes to whoever proposes
                the block, as an incentive to include the transaction sooner.
            </Prose>
            <NumberedFlow steps={[
                ['Wallet estimates fees', 'It reads the current base fee and picks a max fee and a priority fee, capping what the sender is willing to pay per unit of gas.'],
                ['Transaction is broadcast', 'It sits in the mempool until a block proposer picks it up, typically prioritizing higher tips.'],
                ['Block executes', 'The sender pays base fee + priority fee per gas unit actually used; the base fee portion is burned, the tip goes to the proposer.'],
                ['Unused gas limit is refunded', 'Only gas actually consumed is charged: the rest of the gas limit is never touched.'],
            ]} />
        </StageSection>
    )),

    question(2),
];
